import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import getCurrencySymbol from '../lib/currenyUtils';
import { ArrowLeft } from 'lucide-react';

const Paymentqr = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { qrimg, currency, amount, journey_id } = location?.state || {};

  const [timeleft, setTimeleft] = useState(180);

  const minutes = Math.floor(timeleft / 60);
  const seconds = timeleft % 60;
  const formattedtime = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const checkstatus = useCallback(async () => {
    if (!journey_id) return;
    const login_workspace_id = sessionStorage.getItem('login_workspaces_id');
    try {
      const resp = await axios.get(
        `https://banking.fxmaster.co.uk/api/yapily/payments/${journey_id}/status/`,
        {
          headers: {
            accept: '*/*',
            'X-External-User-Id': login_workspace_id,
          },
        },
      );
      const status = (resp.data?.status || '').toUpperCase();
      if (status !== 'PENDING' && status !== 'AWAITING_BANK_SELECTION') {
        navigate('/YapilyStatus', {
          state: {
            status: status,
          },
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [journey_id, navigate]);

  useEffect(() => {
    if (!qrimg) return;
    const timer = setInterval(() => {
      setTimeleft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/YapilyStatus', {
            state: {
              status: 'FAILED',
            },
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const poll = setInterval(checkstatus, 10000);
    checkstatus();
    return () => {
      clearInterval(timer);
      clearInterval(poll);
    };
  }, [qrimg, navigate, checkstatus]);
  if (!qrimg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg text-center">
          <p className="text-gray-600 font-medium">
            Something went wrong. Please try again.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 rounded-lg bg-[#D26521] px-6 py-3 text-white"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
<div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#f8fafc] via-[#eff6ff4d] to-[#eef2ff66] px-4">
      <div className="w-full max-w-lg">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-md mt-2 font-bold text-[#1D2338] flex items-center gap-1"
        >
          <ArrowLeft className='w-5 h-5' />
           Back
        </button>

        <div className="rounded-2xl bg-white p-6 shadow-lg text-center">
        <h2 className="text-2xl font-semibold text-[#1D2338]">Scan To Pay</h2>

        <p className="mt-2 text-gray-500">
          Scan the QR code using your banking app
        </p>

        {amount && currency && qrimg && timeleft > 0 ? (
          <div className="mt-6 flex justify-center">
            <img
              src={qrimg}
              alt="Payment QR Code"
              className="w-64 h-64 object-contain"
            />
          </div>
        ) : amount && currency && timeleft === 0?(
          <div className="mt-8 rounded-lg bg-gray-100 p-8">
            <p className="text-gray-600 font-medium">QR code has expired</p>
          </div>
          ):(
          <div className="mt-8 rounded-lg bg-red-50 p-8">
            <p className="text-red-600 font-medium">Amount or currency is missing.</p>
          </div>
        )}
        {timeleft > 0 && amount && currency && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D26521] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D26521]"></span>
            </span>
            Waiting for confirmation from your bank...
          </div>
        )}
        {amount && currency && (
          <div className="mt-2 font-medium text-[#1D2338]">
            <span className="font-medium">Amount:</span>{' '}
            <span className="font-bold">
              {getCurrencySymbol(currency)}
              {amount}
            </span>
          </div>
        )}
        {amount && currency && timeleft > 0 && (
          <div className="mt-3">
            <p className="text-sm text-gray-500">QR code expires in</p>

            <p className="mt-1 text-2xl font-bold text-[#D26521]">
              {formattedtime}
            </p>
          </div>
        )}
        {amount && currency && timeleft > 0 && (
          <div className="mt-6 text-left">
            <p className="text-[13px] font-semibold text-[#1f2a44] mb-2">How to pay</p>
            <ol className="text-[13px] text-gray-600 space-y-1 list-decimal list-inside">
              <li>Scan the QR code above</li>
              <li>Your bank&apos;s app will open. Authenticate to continue</li>
              <li>Check the payment details and approve the payment in your banking app</li>
            </ol>
          </div>
        )}
        {timeleft === 0 || !amount || !currency && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-6 rounded-lg bg-[#D26521] px-6 py-3 text-white"
          >
            Go Back
          </button>
        )}
        </div>

        <div className="mt-3 bg-[#f7efe9] border border-[#eadfd7] rounded-[14px] p-4 flex gap-3">
          <div className="text-[#d86b1f] text-[16px] mt-[2px]">🔒</div>
          <div>
            <p className="text-[13px] font-[700] text-[#1f2a44]">100% Secure &amp; Safe</p>
            <p className="text-[12px] text-[#7b8190] mt-1 leading-[18px]">
              Your money is protected with bank-level encryption. We never store your banking credentials.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paymentqr;